import { defineStore } from 'pinia'
import { TranslationKey, TranslationUnit } from '@/stores/localization.ts'
import type { ReleaseFilterProps } from '@/utils'
import { computed, ref, shallowRef } from 'vue'

export type Option<T> =
  | {
      label: TranslationKey | TranslationUnit
      value: T
      disabled?: boolean
    }
  | {
      translatedLabel: string
      value: T
      disabled?: boolean
    }

type OptionsSelectState<T> = {
  options: Option<T>[]
  selectedValue: T | null
  label: TranslationKey | TranslationUnit
  onSave: (value: T) => void
}

export const useUiStore = defineStore('ui', () => {
  const _activeLoaders = ref(new Set<string>())
  const isLoading = computed(() => _activeLoaders.value.size > 0)
  const startLoading = (key: string) => {
    const next = new Set(_activeLoaders.value)
    next.add(key)
    _activeLoaders.value = next
  }
  const stopLoading = (key: string) => {
    const next = new Set(_activeLoaders.value)
    next.delete(key)
    _activeLoaders.value = next
  }

  const optionsSelectState = ref<OptionsSelectState<unknown>>({
    options: [],
    selectedValue: null,
    label: 'optionsLoading',
    onSave: () => {},
  })

  const adConsentOpen = ref(false)
  const subscribeToPlanOpen = ref(false)
  const optionsSelectOpen = ref(false)
  const openOptionsSelect = async <T>(state: OptionsSelectState<T>) => {
    optionsSelectOpen.value = true
    optionsSelectState.value = state as OptionsSelectState<unknown>
  }
  const openAdConsent = () => {
    adConsentOpen.value = true
  }
  const openSubscribeToPlan = () => {
    subscribeToPlanOpen.value = true
  }

  type DialogStackEntry = { close: () => void; shake: () => void; dismissable: boolean }
  const dialogStack = shallowRef<DialogStackEntry[]>([])
  const openDialogCount = computed(() => dialogStack.value.length)

  const pushDialog = (entry: DialogStackEntry) => {
    dialogStack.value = [...dialogStack.value, entry]
  }

  const removeDialog = (close: () => void) => {
    dialogStack.value = dialogStack.value.filter((e) => e.close !== close)
  }

  const closeDialogsBlockingNavigation = () => {
    const stack = dialogStack.value
    dialogStack.value = []
    for (let i = stack.length - 1; i >= 0; i--) {
      stack[i].close()
    }
  }

  const handleBackButton = (): boolean => {
    const stack = dialogStack.value
    if (stack.length === 0) return false
    const topmost = stack[stack.length - 1]
    if (topmost.dismissable) {
      topmost.close()
    } else {
      topmost.shake()
    }
    return true
  }
  const filterResetHandler = shallowRef<(() => void) | null>(null)
  const currentFilter = shallowRef<ReleaseFilterProps | null>(null)
  const pendingDatabaseFilter = shallowRef<Partial<ReleaseFilterProps> | null>(null)
  const filtersActive = computed(() => filterResetHandler.value !== null)

  const setFilterReset = (handler: (() => void) | null, filter?: ReleaseFilterProps | null) => {
    filterResetHandler.value = handler
    currentFilter.value = filter ?? null
  }

  const resetFilters = () => {
    filterResetHandler.value?.()
  }

  const setPendingDatabaseFilter = (filter: Partial<ReleaseFilterProps>) => {
    pendingDatabaseFilter.value = { ...filter }
  }

  const consumePendingDatabaseFilter = (): Partial<ReleaseFilterProps> | null => {
    const filter = pendingDatabaseFilter.value
    pendingDatabaseFilter.value = null
    return filter
  }

  const explorationResetRequested = ref(false)
  const requestExplorationReset = () => {
    explorationResetRequested.value = true
  }
  const consumeExplorationReset = (): boolean => {
    if (!explorationResetRequested.value) return false
    explorationResetRequested.value = false
    return true
  }

  const horizontalScrolling = ref(false)

  const preventContextMenuListener = (event: Event) => {
    event.preventDefault()
  }
  const disableContextMenu = () => {
    window.addEventListener('contextmenu', preventContextMenuListener, { passive: false })
  }
  const enableContextMenu = () => {
    window.removeEventListener('contextmenu', preventContextMenuListener)
  }
  return {
    isLoading,
    startLoading,
    stopLoading,
    optionsSelectState,
    openOptionsSelect,
    optionsSelectOpen,
    subscribeToPlanOpen,
    openSubscribeToPlan,
    disableContextMenu,
    enableContextMenu,
    adConsentOpen,
    openAdConsent,
    openDialogCount,
    dialogStack,
    pushDialog,
    removeDialog,
    closeDialogsBlockingNavigation,
    handleBackButton,
    horizontalScrolling,
    filtersActive,
    currentFilter,
    setFilterReset,
    resetFilters,
    setPendingDatabaseFilter,
    consumePendingDatabaseFilter,
    explorationResetRequested,
    requestExplorationReset,
    consumeExplorationReset,
  }
})
